import { z } from "zod";
import { z as zV4 } from "zod/v4";
import type { pictureConfigurationSchema } from "./picture.ts";
import type { unixTimestampSchema } from "./time.ts";
import type { GenericPosition, GenericSize } from "./geom.ts";

const faceBoundingBoxSchema = z.object({
	x: z.number().min(0).max(1),
	y: z.number().min(0).max(1),
	width: z.number().min(0).max(1),
	height: z.number().min(0).max(1),
	confidence: z.number().min(0).max(100).optional(),
	imageQuality: z.object({
		brightness: z.number().min(0).max(100).optional(),
		sharpness: z.number().min(0).max(100).optional(),
	}),
	pose: z.object({
		pitch: z.number().min(-180).max(180).optional(),
		roll: z.number().min(-180).max(180).optional(),
		yaw: z.number().min(-180).max(180).optional(),
	}),
});

type FaceBoundingBox = z.infer<typeof faceBoundingBoxSchema>;

function unionFaces(
	faces: readonly Pick<FaceBoundingBox, "x" | "y" | "width" | "height">[],
): GenericPosition<number> & GenericSize<number> {
	const xMin = Math.min(...faces.map((f) => f.x));
	const yMin = Math.min(...faces.map((f) => f.y));
	const xMax = Math.max(...faces.map((f) => f.x + f.width));
	const yMax = Math.max(...faces.map((f) => f.y + f.height));
	return { x: xMin, y: yMin, width: xMax - xMin, height: yMax - yMin };
}

/** @deprecated */
const whoForSchemaLegacy = z.enum(["myself", "someone_else"]);
/** @deprecated */
const giftTypeSchemaLegacy = z.enum(["brick_mosaic", "gift_card"]);

const whoForSchema = zV4.enum(["myself", "someone_else"]);
const giftTypeSchema = zV4.enum(["brick_mosaic", "gift_card"]);

type WhoFor = z.infer<typeof whoForSchemaLegacy>;
type GiftType = z.infer<typeof giftTypeSchemaLegacy>;

type Project = {
	readonly currentPicture: z.output<typeof pictureConfigurationSchema>;
	readonly otherVersions: readonly z.output<
		typeof pictureConfigurationSchema
	>[];
	readonly whoFor?: WhoFor;
	readonly giftType?: GiftType;
	readonly updatedAt: z.output<typeof unixTimestampSchema>;
};

export {
	unionFaces,
	whoForSchema,
	giftTypeSchema,
	faceBoundingBoxSchema,
	whoForSchemaLegacy,
	giftTypeSchemaLegacy,
};
export type { Project, FaceBoundingBox, WhoFor, GiftType };
